import { useCallback, useState } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'react-i18next'

import { useDeleteProductMessageMutation } from '@/graphql/generated/hooks'
import { DeleteProductMessageInput } from '@/graphql/generated/schemas'

/**
 * Return type for useDeleteProductMessage hook
 */
export interface UseDeleteProductMessageReturnIProps {
  deleteProductMessage: (
    input: DeleteProductMessageInput
  ) => Promise<{ success: boolean; error?: unknown }>
  isDeleting: boolean
}

interface UseDeleteProductMessageOptionsIProps {
  /**
   * Render toasts with the dark-package Alert. The caller (the rebrand
   * AnnouncementBannerSection) opts in; the legacy banner leaves it off so it
   * keeps the legacy toast component, since this hook is shared by both.
   */
  isRebrand?: boolean
}

/**
 * Hook that handles deletion of product messages with proper error handling.
 */
export const useDeleteProductMessage = ({
  isRebrand = false,
}: UseDeleteProductMessageOptionsIProps = {}): UseDeleteProductMessageReturnIProps => {
  const { t } = useTranslation()
  const [deleteMutation] = useDeleteProductMessageMutation()
  const colorScheme = useLightMode()
  const { showSuccess, showError } = useToasts({ colorScheme, isRebrand })
  const [isDeleting, setIsDeleting] = useState(false)

  const deleteProductMessage = useCallback(
    async (
      input: DeleteProductMessageInput
    ): Promise<{ success: boolean; error?: unknown }> => {
      setIsDeleting(true)

      try {
        const result = await deleteMutation({
          variables: { input },
          refetchQueries: ['ProductMessages'],
          awaitRefetchQueries: true,
        })

        if (!result.data?.deleteProductMessage?.id) {
          throw new Error('No data returned from mutation')
        }

        showSuccess(t('settings:rebrand_general_announcement_delete_success'))

        return { success: true }
      } catch (error) {
        Sentry.captureException(error)
        showError(t('settings:rebrand_general_announcement_delete_error'))

        return { success: false, error }
      } finally {
        setIsDeleting(false)
      }
    },
    [deleteMutation, showSuccess, showError, t]
  )

  return {
    deleteProductMessage,
    isDeleting,
  }
}
